import { Link } from "react-router-dom";
import { QuoteStatusBadge } from "@/components/quotes/quote-status";
import { useLeadQuotes } from "@/hooks/useQuotes";

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * The lead's quote requests, the `LeadOrdersSection` shape. Self-fetching (they
 * are not in the lead-detail payload); the caller renders it only for a client
 * whose plan has `quotes`. Drafts and abandoned drafts are not listed — the
 * repository leaves drafts out, and an abandoned one is a list nobody sent.
 * Hides itself when there is nothing to show.
 */
export function LeadQuotesSection({ leadId }: { leadId: string }): React.JSX.Element | null {
  const { data } = useLeadQuotes(leadId, true);
  const quotes = (data?.items ?? []).filter((q) => q.status !== "abandoned");
  if (quotes.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
        Solicitudes de cotización
      </p>
      <div className="flex flex-col gap-2">
        {quotes.map((q) => (
          <Link
            key={q.id}
            to={`/cotizaciones/${q.id}`}
            className="flex flex-col gap-1.5 rounded-[12px] border border-ink-100 bg-white p-2.5 transition-colors hover:bg-ink-050"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-ink-900">#{q.number}</span>
              <span className="text-xs text-ink-600">{formatDate(q.submittedAt)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <QuoteStatusBadge status={q.status} />
              <span className="text-[11px] text-ink-500">
                {q.lineCount} {q.lineCount === 1 ? "línea" : "líneas"}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
